import type { WebSocketEnvelope } from './types';

type WebSocketHandler = (event: WebSocketEnvelope) => void;
type Matcher = (type: string) => boolean;

interface MatcherEntry {
  match: Matcher;
  handler: WebSocketHandler;
}

export class WebSocketEventRouter {
  private readonly exactHandlers = new Map<string, WebSocketHandler[]>();

  private readonly matcherHandlers: MatcherEntry[] = [];

  private fallback: ((event: WebSocketEnvelope) => void) | null = null;

  on(type: string, handler: WebSocketHandler): () => void {
    const handlers = this.exactHandlers.get(type) ?? [];
    handlers.push(handler);
    this.exactHandlers.set(type, handlers);

    return () => {
      const current = this.exactHandlers.get(type);
      if (!current) {
        return;
      }
      const index = current.indexOf(handler);
      if (index >= 0) {
        current.splice(index, 1);
      }
      if (current.length === 0) {
        this.exactHandlers.delete(type);
      }
    };
  }

  onMatch(matcher: Matcher, handler: WebSocketHandler): () => void {
    const entry: MatcherEntry = { match: matcher, handler };
    this.matcherHandlers.push(entry);

    return () => {
      const index = this.matcherHandlers.indexOf(entry);
      if (index >= 0) {
        this.matcherHandlers.splice(index, 1);
      }
    };
  }

  setFallback(handler: ((event: WebSocketEnvelope) => void) | null): void {
    this.fallback = handler;
  }

  clear(): void {
    this.exactHandlers.clear();
    this.matcherHandlers.splice(0, this.matcherHandlers.length);
    this.fallback = null;
  }

  handle(event: WebSocketEnvelope): boolean {
    let handled = false;

    const exact = this.exactHandlers.get(event.type) ?? [];
    if (exact.length > 0) {
      exact.forEach((handler) => handler(event));
      handled = true;
    }

    if (this.matcherHandlers.length > 0) {
      this.matcherHandlers.forEach(({ match, handler }) => {
        if (match(event.type)) {
          handler(event);
          handled = true;
        }
      });
    }

    if (!handled && this.fallback) {
      this.fallback(event);
    }

    return handled;
  }
}

export const createWebSocketEventRouter = () => new WebSocketEventRouter();
